import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import {
  MANUAL_TRANSFORMATIONS,
  Transformation,
  TRANSFORMATION_LABELS,
  TransformationType,
} from "./types";
import { SpecsImageAdjustmentRow } from "./SpecsImageAdjustmentRow";

interface SpecsImageAdjustmentsPanelProps {
  transformations: Transformation[];
  addedTypes: Set<TransformationType>;
  backgroundColor: string | null;
  disabled?: boolean;
  onAddTransformation: (type: TransformationType) => void;
  onRemoveTransformation: (transformId: string) => void;
  onUpdateTransformation: (
    transformId: string,
    patch: Partial<{
      enabled: boolean;
      params: Record<string, number | string>;
    }>
  ) => void;
  onUpdateBackgroundColor: (color: string | null) => void;
}

export function SpecsImageAdjustmentsPanel({
  transformations,
  addedTypes,
  backgroundColor,
  disabled = false,
  onAddTransformation,
  onRemoveTransformation,
  onUpdateTransformation,
  onUpdateBackgroundColor,
}: SpecsImageAdjustmentsPanelProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasBgWhite = backgroundColor === "#ffffff";
  const hasBgBlack = backgroundColor === "#000000";

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            textTransform: "none",
            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.38) !important",
            },
            "&.Mui-disabled .MuiButton-startIcon": {
              color: "rgba(0, 0, 0, 0.38) !important",
            },
          }}
        >
          Add adjustment
        </Button>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={!disabled && !!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            onUpdateBackgroundColor(hasBgWhite ? null : "#ffffff");
            setMenuAnchor(null);
          }}
          sx={{ fontSize: "0.8125rem" }}
        >
          {hasBgWhite && (
            <ListItemIcon sx={{ minWidth: 28 }}>
              <CheckIcon fontSize="small" />
            </ListItemIcon>
          )}
          <ListItemText inset={!hasBgWhite}>White background</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onUpdateBackgroundColor(hasBgBlack ? null : "#000000");
            setMenuAnchor(null);
          }}
          sx={{ fontSize: "0.8125rem" }}
        >
          {hasBgBlack && (
            <ListItemIcon sx={{ minWidth: 28 }}>
              <CheckIcon fontSize="small" />
            </ListItemIcon>
          )}
          <ListItemText inset={!hasBgBlack}>Black background</ListItemText>
        </MenuItem>
        <Divider />
        {MANUAL_TRANSFORMATIONS.map((type) => {
          const active = addedTypes.has(type);
          return (
            <MenuItem
              key={type}
              onClick={() => {
                if (active) {
                  const transformation = transformations.find(
                    (tr) => tr.type === type
                  );
                  if (transformation) onRemoveTransformation(transformation.id);
                } else {
                  onAddTransformation(type);
                }
                setMenuAnchor(null);
              }}
              sx={{ fontSize: "0.8125rem" }}
            >
              {active && (
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText inset={!active}>
                {TRANSFORMATION_LABELS[type]}
              </ListItemText>
            </MenuItem>
          );
        })}
      </Menu>

      {transformations.map((transformation) => (
        <SpecsImageAdjustmentRow
          key={transformation.id}
          transformation={transformation}
          onRemove={() => onRemoveTransformation(transformation.id)}
          onUpdate={(patch) => onUpdateTransformation(transformation.id, patch)}
        />
      ))}
    </Box>
  );
}
